import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Paper, TextField, Button, Spinner, Alert, Chip, EmptyState } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { DANGER_BTN, NEUTRAL_BTN } from '../lib/buttonStyles';
import {
  adminApiClient,
  AdminApiError,
  type MissingUpcItem,
  type ResolveInput,
} from '../lib/adminApiClient';

/**
 * Missing-UPC fix queue (Tier 0, first feature).
 *
 * - Admins (missingupc:resolve) see a resolve form per item, plus approve/reject
 *   on any trusted suggestion.
 * - Trusted contributors (missingupc:submit only) see a suggest form; their
 *   input lands in the queue and never mutates the catalog until an admin approves.
 *
 * Server is the source of truth — this UI only shows controls the caller's
 * capabilities allow; the admin-api re-checks every action.
 */
export default function MissingUpcQueue() {
  const caps = useCapabilities();
  const canResolve = caps.has('missingupc:resolve');
  const canApprove = caps.has('missingupc:approve');
  const canSubmit = caps.has('missingupc:submit');

  const [items, setItems] = useState<MissingUpcItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listMissingUpc('pending');
      setItems(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onActionDone = (kind: 'success' | 'error', msg: string) => {
    setFlash({ kind, msg });
    void load();
  };

  if (caps.loading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Missing UPC queue
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        {items.length} item{items.length === 1 ? '' : 's'} awaiting review.
      </Typography>

      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind === 'success' ? 'success' : 'error'}>{flash.msg}</Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {items.length === 0 ? (
        <EmptyState icon="check_circle" title="All caught up" body="No products are waiting for a UPC." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((item) => (
            <MissingUpcRow
              key={item.id}
              item={item}
              canResolve={canResolve}
              canApprove={canApprove}
              canSubmit={canSubmit}
              onDone={onActionDone}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function MissingUpcRow({
  item,
  canResolve,
  canApprove,
  canSubmit,
  onDone,
}: {
  item: MissingUpcItem;
  canResolve: boolean;
  canApprove: boolean;
  canSubmit: boolean;
  onDone: (kind: 'success' | 'error', msg: string) => void;
}) {
  // Entry only — never prefilled from a stored/suggested UPC (feedback_never_show_upc).
  const [upc, setUpc] = useState('');
  const [size, setSize] = useState(item.suggestion?.size ?? '');
  const [imageUrl, setImageUrl] = useState(item.suggestion?.imageUrl ?? '');
  const [busy, setBusy] = useState(false);

  const product = item.product;
  const upcValid = /^\d{12,13}$/.test(upc.trim());

  const run = async (fn: () => Promise<unknown>, okMsg: string) => {
    setBusy(true);
    try {
      await fn();
      onDone('success', okMsg);
    } catch (err) {
      const msg = err instanceof AdminApiError ? err.message : 'Action failed';
      onDone('error', msg);
    } finally {
      setBusy(false);
    }
  };

  const input = (): ResolveInput => ({
    upc: upc.trim(),
    size: size.trim() || undefined,
    imageUrl: imageUrl.trim() || undefined,
  });

  return (
    <Paper sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box
          component="img"
          src={product?.primaryImageUrl ?? ''}
          alt=""
          sx={{
            width: 64,
            height: 64,
            objectFit: 'cover',
            borderRadius: 1,
            bgcolor: '#eee',
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {product?.itemName ?? '(unlinked product)'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {[product?.brand, product?.descriptionShort].filter(Boolean).join(' · ') || '—'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip label={`source: ${item.source}`} size="small" />
            <Chip label={`attempts: ${item.attemptCount}`} size="small" />
            {item.suggestion && (
              <Chip label="suggestion pending" color="warning" size="small" />
            )}
          </Box>
        </Box>
      </Box>

      {(canResolve || canSubmit) && (
        <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            label="UPC (12–13 digits)"
            value={upc}
            onChange={(e) => setUpc(e.target.value)}
            size="small"
            error={upc.length > 0 && !upcValid}
            sx={{ width: 200 }}
          />
          <TextField
            label="Size (e.g. 32 oz)"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            size="small"
            sx={{ width: 160 }}
          />
          <TextField
            label="Image URL"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
          />
          {canResolve ? (
            <Button
              variant="contained"
              ariaLabel="Resolve missing UPC"
              buttonText="Resolve"
              disabled={busy || !upcValid}
              onClick={() => run(() => adminApiClient.resolveMissingUpc(item.id, input()), 'Resolved.')}
            />
          ) : (
            <Button
              variant="contained"
              ariaLabel="Suggest UPC resolution"
              buttonText="Suggest"
              disabled={busy || !upcValid}
              onClick={() => run(() => adminApiClient.suggestMissingUpc(item.id, input()), 'Suggestion submitted.')}
            />
          )}
        </Box>
      )}

      {canApprove && item.suggestion && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button
            variant="outlined"
            sx={NEUTRAL_BTN}
            color="primary"
            ariaLabel="Approve suggestion"
            buttonText="Approve suggestion"
            disabled={busy}
            onClick={() => run(() => adminApiClient.approveMissingUpc(item.id), 'Suggestion approved.')}
          />
          <Button
            variant="outlined"
            sx={DANGER_BTN}
            color="error"
            ariaLabel="Reject suggestion"
            buttonText="Reject"
            disabled={busy}
            onClick={() => run(() => adminApiClient.rejectMissingUpc(item.id), 'Suggestion rejected.')}
          />
        </Box>
      )}
    </Paper>
  );
}
